import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import { useGroupedInvoices, useExportInvoices } from '../../../hooks/useInvoices';
import { useCouponsDetails } from '../../../hooks/useCoupons';
import { ITEMS_PER_PAGE } from '../constants/paymentsTableConstants';
import { exportInvoicesExcel } from '../utils/exportInvoicesExcel';

export const useViewPaymentsTable = () => {
  const theme = useTheme();
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    entidadSeleccionada: 'todas',
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);
  const [openDetails, setOpenDetails] = useState(false);
  const [dialogInfo, setDialogInfo] = useState({});
  const [detailsData, setDetailsData] = useState([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [searchParams, setSearchParams] = useState(null);

  // Query pagos agrupados (manual)
  const groupedInvoicesQuery = useGroupedInvoices(searchParams || {});
  const { data: groupedData = [], isLoading, refetch } = groupedInvoicesQuery;
  // Mutation detalles de cupones
  const couponsDetailsMutation = useCouponsDetails();
  const exportInvoicesMutation = useExportInvoices();

  const totalPages = Math.max(1, Math.ceil((groupedData.length || 0) / rowsPerPage));
  const currentPageData = groupedData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleFilter = async () => {
    // Mapea los filtros al formato esperado por el backend
    setSearchParams({
      start_date: filters.startDate,
      end_date: filters.endDate,
      legal_entity: filters.entidadSeleccionada,
    });
    await refetch();
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value)) {
      setRowsPerPage(value);
      setPage(1);
    }
  };

  const handleOpenDetails = async (couponsUUIDs, paymentDate, invoiceDate, collectorEntity) => {
    setIsLoadingDetails(true);
    setDialogInfo({ paymentDate, invoiceDate, collectorEntity });
    try {
      const result = await couponsDetailsMutation.mutateAsync(couponsUUIDs);
      setDetailsData(result);
      setOpenDetails(true);
    } catch (error) {
      setDetailsData([]);
      setOpenDetails(true);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
    setDetailsData([]);
  };

  const handleExportInvoices = async () => {
    if (!filters.startDate || !filters.endDate) {
      return;
    }

    const params = {
      start_date: filters.startDate,
      end_date: filters.endDate,
      legal_entity: filters.entidadSeleccionada,
    };

    try {
      const invoices = await exportInvoicesMutation.mutateAsync(params);
      exportInvoicesExcel(invoices);
    } catch (error) {
      console.error('Error exporting invoices:', error);
    }
  };

  return {
    filters,
    setFilters,
    data: currentPageData,
    isLoading: isLoading || isLoadingDetails,
    page,
    totalPages,
    totalCount: groupedData.length || 0,
    rowsPerPage,
    handleFilter,
    handlePageChange,
    handleRowsPerPageChange,
    handleExportInvoices,
    openDetails,
    handleOpenDetails,
    handleCloseDetails,
    detailsData,
    dialogInfo,
    theme,
    isExporting: exportInvoicesMutation.isPending,
  };
};
